import { SaveOutlined } from '@ant-design/icons';
import { Checkbox, Col, Form, Input, message, Modal, Row, Select, Spin } from 'antd';
import React, { useState } from 'react';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import { useTranslation } from 'react-i18next';
import { ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import { EmployeeType } from '@/constants';
import CreateBtn from '@/components/app/CreateBtn';
import MainContent from '@/layouts/MainContent';
import { Helmet } from 'react-helmet';
import { defaultApiErrorHandle } from '@/helpers/errorHandler';
import { CompanyUserDto } from '@/__generated';
import { apiSdk } from '@/httpClient';

const PAGE_TITLE = 'Company Roles';

const columns: IColumnType<CompanyUserDto>[] = [
  {
    title: 'companyRolesPage_RoleName',
    dataIndex: 'name',
    ellipsis: true,
  },
  {
    title: 'companyRolesPage_NormalizedName',
    dataIndex: 'normalizedName',
    ellipsis: true,
  },
];

const filters: Array<IFilterItem> = [{ type: 'TEXT', name: 'name', label: 'companyRolesPage_RoleName' }];

const CompanyRolePage = () => {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = React.useState<Array<{ value: any; label: any }>>([]);
  const [permissions, setPermissions] = React.useState<Array<{ value: any; label: any }>>([]);

  React.useEffect(() => {
    apiSdk.RoleService.getCompanyRoleNames().then(res => {
      setRoles(res.map(x => ({ value: x, label: x })));
    });
    apiSdk.RoleService.getCompanyPermissions().then(res => {
      setPermissions(res?.map(x => ({ value: x, label: x })) || []);
    });
  }, []);

  const createEntity = values => {
    setLoading(true);
    apiSdk.RoleService.createCompanyRole({ body: { ...values, IsTransportRole: true } })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          defaultApiErrorHandle(response);
        }
      })
      .catch(error => {
        defaultApiErrorHandle(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const updateEntity = values => {
    setLoading(true);
    apiSdk.RoleService.updateCompanyRole({ body: { ...values, IsTransportRole: true } })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          defaultApiErrorHandle(response);
        }
      })
      .catch(error => {
        defaultApiErrorHandle(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const onEdit = (id: string) => {
    setLoading(true);
    apiSdk.RoleService.getRoleDetails({ id: id })
      .then(res => {
        if (res) {
          setIsNewForm(false);
          setFormVisible(true);
          form.setFieldsValue({
            ...res,
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <MainContent
      isLoading={loading}
      title={t(PAGE_TITLE)}
      extraBtns={
        <CreateBtn
          onClick={() => {
            setIsNewForm(true);
            setFormVisible(true);
            form.resetFields();
          }}
        />
      }
    >
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <ZTable<CompanyUserDto>
          columns={columns}
          primaryKey="id"
          tableRef={zTb}
          defaultVariables={{
            employeeType: EmployeeType.Employee,
          }}
          url="/user-accounts/roles/get-company-roles"
          bordered
          order_by="name"
          order_by_asc={true}
          allowEdit={true}
          onEdit={record => {
            onEdit(record.id || '');
          }}
          allowDelete={true}
          delete_url="/user-accounts/roles/delete"
          filters={filters}
          searchBtnAlignLeft
        />
        {formVisible ? (
          <Modal
            open={formVisible}
            maskClosable={false}
            width={ModalWidth.small}
            className="nta-custom-form"
            title={isNewForm ? t('companyRolesPage_AddNewRole') : t('companyRolesPage_UpdateRole')}
            forceRender={true}
            style={{ top: 10 }}
            okText={t('save')}
            cancelText={t('cancel')}
            okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
            onCancel={() => {
              form.resetFields();
              setFormVisible(false);
            }}
            onOk={() => {
              form
                .validateFields()
                .then(values => {
                  if (values.id) {
                    updateEntity(values);
                  } else {
                    createEntity(values);
                  }
                })
                .catch(info => {});
            }}
          >
            <Spin spinning={loading}>
              <Form form={form} size="middle" layout="horizontal" labelAlign="right">
                <Form.Item name="id" className="hidden">
                  <Input type="hidden" />
                </Form.Item>
                <Row gutter={ROW_GUTTER}>
                  <Col md={24}>
                    <Form.Item label={t('companyRolesPage_RoleName')} name="name" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <Select options={roles} disabled={!isNewForm} className="input-value-color" />
                    </Form.Item>
                    <b>{t('companyRolesPage_Permissions')}</b>
                    <Form.Item name="permissions">
                      <Checkbox.Group>
                        <Row gutter={ROW_GUTTER}>
                          {permissions.map(p => {
                            return (
                              <Col md={12} key={p.value}>
                                <Checkbox value={p.value}>{p.label}</Checkbox>
                              </Col>
                            );
                          })}
                        </Row>
                      </Checkbox.Group>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Spin>
          </Modal>
        ) : null}
      </div>
    </MainContent>
  );
};
export default CompanyRolePage;
